import { ProjectCheckFinding, ProjectCheckResult } from '../../types';

/**
 * Was die Anzeige des Projektchecks aus den Befunden macht.
 *
 * Seit es gruene Befunde gibt (Regel 17 "Offene Sicherheitseinbehalte",
 * docs/sicherheitseinbehalt-plan.md §10) reicht `findings` als eine Liste nicht
 * mehr: die Ansicht malte bisher alles, was nicht rot war, bernsteinfarben, gab
 * ihm ein Warndreieck und zaehlte es in den Chip. Eine Zahl, die den vertraglich
 * vereinbarten Zustand mitteilt, saehe damit aus wie ein Fehler — und die
 * Ampel, die daneben auf Gruen steht, widerspraeche ihr.
 *
 * Die Trennung steht hier und nicht in der Ansicht, weil es im Projekt keine
 * DOM-Testumgebung gibt (siehe paymentsLoadLogic.ts): pur ist sie pruefbar, in
 * JSX waere sie es nicht.
 */

/** Rot und Gelb — das, was jemand abarbeiten oder quittieren muss. */
export const isIssueFinding = (f: ProjectCheckFinding): boolean => f.traffic !== 'green';

export interface SplitFindings {
    /** Rot und Gelb, in der Reihenfolge der Regeln. */
    issues: ProjectCheckFinding[];
    /** Gruen: Hinweiszeilen, die eine Zahl mitteilen und nichts fordern. */
    notices: ProjectCheckFinding[];
}

/**
 * Die einzige Zaehlung: `issues.length` ist die Zahl am Chip und in der
 * Sammelansicht. Eine zweite Zaehlfunktion daneben waere die naechste Stelle,
 * die man beim Hinzufuegen einer Ampelfarbe vergisst.
 */
export const splitCheckFindings = (findings: ProjectCheckFinding[]): SplitFindings => ({
    issues: findings.filter(isIssueFinding),
    notices: findings.filter((f) => !isIssueFinding(f)),
});

/**
 * Der Satz "keine Auffälligkeiten" — er bleibt auch dann stehen, wenn
 * Hinweiszeilen da sind.
 *
 * Er verschwand bisher, sobald irgendein Befund existierte. Mit gruenen
 * Befunden waere das falsch: der Beleg IST sauber, es steht nur eine Zahl
 * daneben. Umgekehrt duerfte die Vollaussage "Leistungsstand, Berichte und
 * Abrechnung passen zusammen" nicht allein stehen bleiben, wenn darunter eine
 * Zeile ueber offene Sicherheitseinbehalte folgt — der Leser haelt sie sonst
 * fuer uebersehen. Deshalb der Zusatzsatz: er benennt die Hinweiszeilen als
 * das, was sie sind, und nimmt ihnen den Anschein einer Forderung.
 */
export const allClearText = (
    result: Pick<ProjectCheckResult, 'reportExempt' | 'hoursOnly'>,
    hasNotices: boolean,
): string => {
    // Die Vollaussage darf nur stehen, wenn der Berichtsabgleich auch wirklich
    // gelaufen ist. Bei Stundenabrechnung fehlt die Stueckposition, gegen die
    // das Archiv liefe — das ist kein Befund, aber auch kein bestandener
    // Abgleich.
    const kern = result.reportExempt
        ? 'Keine Auffälligkeiten bei den geprüften Regeln.'
        : result.hoursOnly
          ? 'Keine Auffälligkeiten. Dieses Projekt wird nach Stunden abgerechnet; der Abgleich von Berichtsarchiv und Leistungsstand entfällt deshalb.'
          : 'Keine Auffälligkeiten. Leistungsstand, Berichte und Abrechnung passen zusammen.';
    return hasNotices ? `${kern} Die Hinweise unten sind zur Kenntnis, nicht zu korrigieren.` : kern;
};
